import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import classnames from "classnames";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";

export interface SelectProps extends RadixSelect.SelectProps {
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      placeholder = "Выберите...",
      className,
      triggerClassName,
      contentClassName,
      itemClassName,
      value,
      defaultValue,
      onValueChange,
      ...props
    },
    ref
  ) => {
    // Если value не задан, по умолчанию выбираем первый элемент
    const initial = value ?? defaultValue ?? (options[0]?.value ?? "");
    const [internalValue, setInternalValue] = React.useState(initial);
    React.useEffect(() => {
      if (value !== undefined) setInternalValue(value);
    }, [value]);
    const handleChange = (val: string) => {
      setInternalValue(val);
      onValueChange?.(val);
    };
    return (
      <RadixSelect.Root
        value={internalValue}
        onValueChange={handleChange}
        {...props}
      >
        <RadixSelect.Trigger
          ref={ref}
          className={classnames(
            "inline-flex items-center justify-between rounded border px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full min-h-[40px]",
            triggerClassName,
            className
          )}
        >
          <RadixSelect.Value placeholder={placeholder} />
          <RadixSelect.Icon className="ml-2">
            <ChevronDownIcon />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            className={classnames(
              "bg-white dark:bg-gray-800 border rounded shadow-lg z-50 dark:border-gray-900",
              contentClassName
            )}
          >
            <RadixSelect.ScrollUpButton className="flex items-center justify-center h-6">
              <ChevronUpIcon />
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className="p-1">
              {options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  className={itemClassName}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton className="flex items-center justify-center h-6">
              <ChevronDownIcon />
            </RadixSelect.ScrollDownButton>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    );
  }
);

interface SelectItemProps extends RadixSelect.SelectItemProps {
  children: React.ReactNode;
  className?: string;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, className, ...props }, forwardedRef) => {
    return (
      <RadixSelect.Item
        className={classnames(
          "flex items-center px-3 py-2 cursor-pointer rounded focus:bg-gray-100 dark:focus:bg-gray-700 outline-none hover:bg-gray-100 dark:hover:bg-gray-600 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-600",
          className
        )}
        {...props}
        ref={forwardedRef}
      >
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
        <RadixSelect.ItemIndicator className="ml-auto">
          <CheckIcon />
        </RadixSelect.ItemIndicator>
      </RadixSelect.Item>
    );
  }
);

Select.displayName = "Select";
SelectItem.displayName = "SelectItem"; 